import { TenantLogo } from '@connect-carrier/components/TenantLogo';
import {
  Roles,
  useHasAnyRole,
  useHasRole,
} from '@connect-carrier/hooks/useHasRole';
import { css, useTheme } from '@emotion/react';

import { RoutePath } from '@app/routes';
import { Flex } from '@components/Flex';
import { Link } from '@components/Link';
import { getSelectedCustomer } from '@connect-carrier/harness/customer';
import { useFetchCustomerList } from '@connect-carrier/hooks/useFetchCustomerList';
import { useTendersCount } from '@connect-carrier/hooks/useTendersCount';
import { ConnectCarrierRoutePath } from '@connect-carrier/routes/defs';
import { useFlagMe400205CarrierPortalTendersFrontend } from '@generated/flags/ME-400205-carrier-portal-tenders-frontend';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';

interface NavLinksProps {
  showDivider?: boolean;
  customerSelectionPage?: boolean;
  toggleDialog?: () => void;
  headerFg?: string;
}

// ts-unused-exports:disable-next-line
export const NavLinks: FC<NavLinksProps> = ({
  showDivider = false,
  toggleDialog,
  headerFg,
}) => {
  const { customerList: customerListOutputs } = useFetchCustomerList();
  const isAdmin = useHasRole(Roles.Admin);
  // const isOperations = useHasRole(Roles.Operations);
  const isReadOnly = useHasRole(Roles.ReadOnly);
  const isSelectedCustomer = getSelectedCustomer();
  const location = useLocation();
  const fromPage = location.state?.fromPage as string;
  const exceptPricingRole = useHasAnyRole([
    Roles.Admin,
    Roles.Accounting,
    Roles.Operations,
    Roles.ReadOnly,
  ]);
  const isPricing = useHasRole(Roles.Pricing);
  const canViewOffers = isAdmin || (isPricing && isReadOnly);
  const isCarrierPortalTendersEnabled =
    useFlagMe400205CarrierPortalTendersFrontend();
  const enableNavigationBrandingSearchFe =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();

  const tendersCount = useTendersCount();

  let links: {
    label: string;
    path: ConnectCarrierRoutePath<string, string>;
    testId: string;
  }[] = isCarrierPortalTendersEnabled
    ? []
    : [
        {
          label: 'My Routes',
          path: '/my-routes',
          testId: 'nav-link-my-routes',
        },
      ];

  if (customerListOutputs.length) {
    if (isCarrierPortalTendersEnabled) {
      if (isPricing || exceptPricingRole) {
        links.push({
          label: `Tenders (${tendersCount.count})`,
          path: '/tenders',
          testId: 'nav-link-tenders',
        });
      }
      if (canViewOffers) {
        links.push({
          label: 'Offers',
          path: '/offers',
          testId: 'nav-link-offers',
        });
      }
      if (isReadOnly || isAdmin) {
        if (isReadOnly) {
          links.push({
            label: 'Offers',
            path: '/offers',
            testId: 'nav-link-offers',
          });
        }
        links.push({
          label: 'Available Routes',
          path: '/available-routes',
          testId: 'nav-link-available-routes',
        });
      }
      links.push({
        label: 'My Routes',
        path: '/my-routes',
        testId: 'nav-link-my-routes',
      });
      if (isPricing) {
        if (exceptPricingRole) {
          links = [
            {
              label: `Tenders (${tendersCount.count})`,
              path: '/tenders',
              testId: 'nav-link-tenders',
            },
            {
              label: 'Offers',
              path: '/offers',
              testId: 'nav-link-offers',
            },
            {
              label: 'Available Routes',
              path: '/available-routes',
              testId: 'nav-link-available-routes',
            },
            {
              label: 'My Routes',
              path: '/my-routes',
              testId: 'nav-link-my-routes',
            },
          ];
        } else {
          links = [
            {
              label: `Tenders (${tendersCount.count})`,
              path: '/tenders',
              testId: 'nav-link-tenders',
            },
            {
              label: 'Offers',
              path: '/offers',
              testId: 'nav-link-offers',
            },
            {
              label: 'Available Routes',
              path: '/available-routes',
              testId: 'nav-link-available-routes',
            },
          ];
        }
      }
    } else {
      if (isAdmin || isReadOnly) {
        links.push({
          label: 'Available Routes',
          path: '/available-routes',
          testId: 'nav-link-available-routes',
        });
        if (isReadOnly) {
          links.push({
            label: 'Offers',
            path: '/offers',
            testId: 'nav-link-offers',
          });
        }
      }
      if (canViewOffers) {
        links.push({
          label: 'Offers',
          path: '/offers',
          testId: 'nav-link-offers',
        });
      }
      if (isPricing) {
        if (exceptPricingRole) {
          links = [
            {
              label: 'My Routes',
              path: '/my-routes',
              testId: 'nav-link-my-routes',
            },
            {
              label: 'Available Routes',
              path: '/available-routes',
              testId: 'nav-link-available-routes',
            },
            {
              label: 'Offers',
              path: '/offers',
              testId: 'nav-link-offers',
            },
          ];
        } else {
          links = [
            {
              label: 'Available Routes',
              path: '/available-routes',
              testId: 'nav-link-available-routes',
            },
            {
              label: 'Offers',
              path: '/offers',
              testId: 'nav-link-offers',
            },
          ];
        }
      }
    }
    if (isAdmin) {
      links.push({
        label: 'User Management',
        path: '/user-management',
        testId: 'nav-link-user-management',
      });
    }
  }

  if (!isSelectedCustomer || !customerListOutputs.length) {
    links = [];
  }

  if (!customerListOutputs.length) {
    if (isAdmin) {
      links.push({
        label: 'User Management',
        path: '/user-management',
        testId: 'nav-link-user-management',
      });
    } else {
      links = [];
    }
  }

  const { gray } = useTheme();

  const isActiveLink = (linkPath: string): boolean => {
    return (
      location.pathname === linkPath ||
      (linkPath === '/available-routes' &&
        location.pathname.includes('/available-route-details')) ||
      (linkPath === '/offers' &&
        location.pathname.includes('/offer-details')) ||
      (linkPath === '/offers' &&
        fromPage === 'offers' &&
        location.pathname.includes('/submit-offer')) ||
      (linkPath === '/available-routes' &&
        fromPage === 'available-routes' &&
        location.pathname.includes('/submit-offer')) ||
      (linkPath === '/accounting' &&
        location.pathname.includes('/accounting-route-details')) ||
      (linkPath === '/my-routes' &&
        location.pathname.includes('/route-details')) ||
      (linkPath === '/tenders' &&
        location.pathname.includes('/tender-details')) ||
      (linkPath === '/my-routes' &&
        location.pathname.includes('/driver') &&
        !location.pathname.includes('/accounting-route-details')) ||
      (linkPath === '/user-management' &&
        location.pathname.includes('/user-details'))
    );
  };
  if (!showDivider) {
    return (
      <Flex
        css={{
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {links.map((link, index) => (
          <Flex
            css={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            key={index}
            data-testid={`nav-link-container-${link.testId}`}
          >
            {enableNavigationBrandingSearchFe ? (
              <TenantLogo
                color={headerFg}
                altText={isSelectedCustomer?.displayName ?? ''}
              />
            ) : (
              <Link
                css={{
                  width: 'auto',
                  textDecoration: 'none',
                  textAlign: 'center',
                }}
                data-testid={link.testId}
                to={link.path as RoutePath<`${string}/`, `${string}/`>}
                onClick={toggleDialog}
              >
                <span
                  css={{
                    textAlign: showDivider ? 'left' : 'center',
                    fontSize: '16px',
                    color: enableNavigationBrandingSearchFe
                      ? headerFg
                      : isActiveLink(link.path)
                        ? gray['30']
                        : gray['60'],
                    fontWeight: isActiveLink(link.path) ? 'bolder' : 'normal',
                  }}
                >
                  {link.label}
                </span>
              </Link>
            )}
            {showDivider && index !== links.length - 1 && (
              <hr
                style={{
                  marginTop: '10px',
                  width: '100%',
                  border: 0,
                  height: '1px',
                  backgroundColor: gray[80],
                }}
                data-testid={`divider-${link.testId}`}
              />
            )}
          </Flex>
        ))}
      </Flex>
    );
  }

  return (
    <>
      {links.map((link, index) => (
        <div
          css={{ width: '100%' }}
          key={index}
          data-testid={`nav-link-container-${link.testId}`}
        >
          <Link
            css={css`
              width: 100%;
              text-decoration: none;
              text-align: center;
            `}
            data-testid={link.testId}
            to={link.path as RoutePath<`${string}/`, `${string}/`>}
            onClick={toggleDialog}
          >
            <span
              css={{
                width: '100%',
                display: 'block',
                textAlign: showDivider ? 'left' : 'center',
                fontSize: '16px',
                fontStretch: 'normal',
                color: enableNavigationBrandingSearchFe
                  ? headerFg
                  : isActiveLink(link.path)
                    ? gray['30']
                    : gray['60'],
                fontWeight: isActiveLink(link.path) ? 'bolder' : 'normal',
              }}
            >
              {link.label}
            </span>
          </Link>
          {showDivider && index !== links.length - 1 && (
            <hr
              style={{
                marginTop: '20px',
                width: '100%',
                border: 0,
                height: '1px',
                backgroundColor: gray[80],
              }}
              data-testid={`divider-${link.testId}`}
            />
          )}
        </div>
      ))}
    </>
  );
};
